import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  Button,
  Input,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  InputGroup,
  InputLeftAddon,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Icon,
  useToast,
} from '@chakra-ui/react';
import { Link, useNavigate } from 'react-router-dom';
import React from 'react';
import useSWR from 'swr';
import getStudentsAdmin from 'api/administrador/searchStudent';
import { FaArrowRight } from 'react-icons/fa';
import { FiUserPlus, FiSearch, FiEye, FiEyeOff } from 'react-icons/fi';
import Loading from 'components/loading/Loading';
import useAuth from 'hooks/useAuth';
import xhr from 'xhr';
import settings from '../../../settings';
import { authHeaders } from 'utils/jwt';

const COUNTRIES = [
  { id: 53, name: 'Colombia', code: '+57' },
  { id: 241, name: 'Estados Unidos', code: '+1' },
  { id: 147, name: 'Mexico', code: '+52' },
  { id: 245, name: 'Venezuela', code: '+58' },
  { id: 68, name: 'Ecuador', code: '+593' },
  { id: 177, name: 'Peru', code: '+51' },
  { id: 49, name: 'Chile', code: '+56' },
  { id: 13, name: 'Argentina', code: '+54' },
  { id: 214, name: 'Espana', code: '+34' },
  { id: 174, name: 'Panama', code: '+507' },
  { id: 58, name: 'Costa Rica', code: '+506' },
  { id: 102, name: 'Honduras', code: '+504' },
  { id: 94, name: 'Guatemala', code: '+502' },
  { id: 28, name: 'Bolivia', code: '+591' },
  { id: 67, name: 'Republica Dominicana', code: '+1809' },
  { id: 176, name: 'Paraguay', code: '+595' },
  { id: 242, name: 'Uruguay', code: '+598' },
  { id: 70, name: 'El Salvador', code: '+503' },
  { id: 162, name: 'Nicaragua', code: '+505' },
  { id: 33, name: 'Brasil', code: '+55' },
];

export default function SchoolStudents() {
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [creating, setCreating] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [emailError, setEmailError] = React.useState('');

  const [newStudent, setNewStudent] = React.useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    cellphone: '',
    country: 53,
  });

  const { data, error, isLoading, mutate } = useSWR(
    { type: 'getStudents', user: user },
    getStudentsAdmin,
    { revalidateOnFocus: false }
  );

  // Debounce search input (400ms)
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.toLowerCase().trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Client-side filtering
  const filteredStudents = React.useMemo(() => {
    if (!data) return [];
    if (!debouncedSearch) return data;

    return data.filter((el) => {
      if (!el.user) return false;
      const term = debouncedSearch;
      const id = String(el.id);
      const name = `${el.user.first_name || ''} ${el.user.last_name || ''}`.toLowerCase();
      const email = (el.user.email || '').toLowerCase();
      const cellphone = (el.user.cellphone || '').toLowerCase();

      return (
        id.includes(term) ||
        name.includes(term) ||
        email.includes(term) ||
        cellphone.includes(term)
      );
    });
  }, [data, debouncedSearch]);

  const handleCreate = async () => {
    if (!newStudent.first_name || !newStudent.email || !newStudent.password || !newStudent.cellphone) {
      toast({
        title: 'Campos requeridos',
        description: 'Nombre, email, celular y contrasena son obligatorios',
        status: 'warning',
        position: 'bottom',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      setCreating(true);

      // Con el JWT del staff el API no exige CAPTCHA (ver register-guard en klazia-api).
      const response = await fetch(`${settings.api}/api/auth/local/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          first_name: newStudent.first_name,
          last_name: newStudent.last_name,
          email: newStudent.email,
          password: newStudent.password,
          cellphone: newStudent.cellphone,
          country: newStudent.country,
          user_type: 'student',
          schoolFrom: String(user?.school?.id),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errId = errorData?.error?.details?.id || errorData?.error?.message || '';

        if (errId.includes('email.taken')) {
          setEmailError('Este correo ya esta registrado');
          setCreating(false);
          return;
        }
        if (errId.includes('cellphone.taken')) {
          toast({
            title: 'Celular ya registrado',
            description: 'Este numero de celular ya esta asociado a otra cuenta',
            status: 'warning',
            position: 'bottom',
            duration: 5000,
            isClosable: true,
          });
          setCreating(false);
          return;
        }

        throw new Error(errorData?.error?.message || 'Error al registrar estudiante');
      }

      const resData = await response.json();
      const userId = resData.user?.id;

      // Look up the student entity by user ID to get the student ID
      let studentId = null;
      if (userId) {
        try {
          const studentRes = await xhr(`/students?filters[user][id][$eq]=${userId}&fields[0]=id`);
          studentId = studentRes.data?.data?.[0]?.id;
        } catch (e) {
          // If lookup fails, stay on the list
        }
      }

      toast({
        title: 'Estudiante registrado exitosamente',
        description: `${newStudent.first_name} ${newStudent.last_name}`,
        status: 'success',
        position: 'bottom',
        duration: 4000,
        isClosable: true,
      });

      setNewStudent({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        cellphone: '',
        country: 53,
      });
      onClose();
      mutate();

      // Redirect to student detail
      if (studentId) {
        navigate(`/plataforma/school/ver-estudiante/${studentId}`);
      }
    } catch (error) {
      toast({
        title: 'Error al registrar estudiante',
        description: error.message || 'Intenta de nuevo',
        status: 'error',
        position: 'bottom',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  const selectedCountry = COUNTRIES.find((c) => c.id === Number(newStudent.country));

  if (!data) return <Loading />;

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={5}>
        <Text fontSize="24px" fontWeight="bold">
          Estudiantes
        </Text>
        <Button
          leftIcon={<FiUserPlus />}
          backgroundColor="#574feb"
          colorScheme="messenger"
          size="md"
          onClick={onOpen}
        >
          Registrar estudiante
        </Button>
      </Flex>

      <InputGroup mb={4} maxW="400px">
        <InputLeftElement pointerEvents="none">
          <Icon as={FiSearch} color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder="Buscar por ID, nombre, correo o celular..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          bg="white"
          borderRadius="lg"
        />
      </InputGroup>

      <Box
        backgroundColor="white"
        borderRadius="10px"
        border="1px solid"
        borderColor="gray.200"
        overflow="hidden"
      >
        {isLoading ? (
          <Flex justify="center" py={10}>
            <Spinner size="lg" />
          </Flex>
        ) : filteredStudents.length === 0 ? (
          <Box p="20px" textAlign="center" color="gray.500">
            {debouncedSearch
              ? 'No se encontraron estudiantes para tu busqueda'
              : 'No hay estudiantes registrados'}
          </Box>
        ) : (
          <TableContainer>
            <Table variant="striped" size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th textAlign="left">ID</Th>
                  <Th>Nombres</Th>
                  <Th>Email</Th>
                  <Th>Celular</Th>
                  <Th>Ver</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredStudents.map((el) => {
                  if (!el.user) return null;

                  return (
                    <Tr key={el.id}>
                      <Td textAlign="left">{el.id}</Td>
                      <Td>
                        {el.user.first_name} {el.user.last_name}
                      </Td>
                      <Td>{el.user.email}</Td>
                      <Td>{el.user.cellphone}</Td>
                      <Td>
                        <Link to={`/plataforma/school/ver-estudiante/${el.id}`}>
                          <Button colorScheme="messenger" background="#574feb" size="sm">
                            <FaArrowRight size="10px" />
                          </Button>
                        </Link>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Modal: Registrar Estudiante */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>Registrar nuevo estudiante</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap={4}>
              <Flex gap={4}>
                <FormControl isRequired flex="1">
                  <FormLabel>Nombres</FormLabel>
                  <Input
                    value={newStudent.first_name}
                    onChange={(e) => setNewStudent((p) => ({ ...p, first_name: e.target.value }))}
                    placeholder="Nombres"
                  />
                </FormControl>
                <FormControl flex="1">
                  <FormLabel>Apellidos</FormLabel>
                  <Input
                    value={newStudent.last_name}
                    onChange={(e) => setNewStudent((p) => ({ ...p, last_name: e.target.value }))}
                    placeholder="Apellidos"
                  />
                </FormControl>
              </Flex>

              <Flex gap={4}>
                <FormControl flex="1">
                  <FormLabel>Pais</FormLabel>
                  <Select
                    value={newStudent.country}
                    onChange={(e) => setNewStudent((p) => ({ ...p, country: Number(e.target.value) }))}
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.code})
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired flex="1">
                  <FormLabel>Celular</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>{selectedCountry?.code || '+57'}</InputLeftAddon>
                    <Input
                      type="tel"
                      value={newStudent.cellphone}
                      onChange={(e) =>
                        setNewStudent((p) => ({ ...p, cellphone: e.target.value.replace(/\D/g, '') }))
                      }
                      placeholder="Numero de celular"
                    />
                  </InputGroup>
                </FormControl>
              </Flex>

              <FormControl isRequired isInvalid={!!emailError}>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={newStudent.email}
                  onChange={(e) => {
                    setEmailError('');
                    setNewStudent((p) => ({ ...p, email: e.target.value }));
                  }}
                  placeholder="correo@ejemplo.com"
                  borderColor={emailError ? 'red.400' : undefined}
                />
                {emailError && (
                  <Text fontSize="xs" color="red.500" mt={1}>
                    {emailError}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Contrasena</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newStudent.password}
                    onChange={(e) => setNewStudent((p) => ({ ...p, password: e.target.value }))}
                    placeholder="Contrasena para el estudiante"
                  />
                  <InputRightElement>
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={showPassword ? <FiEyeOff /> : <FiEye />}
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Ocultar contrasena' : 'Ver contrasena'}
                    />
                  </InputRightElement>
                </InputGroup>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  El estudiante usara este email y contrasena para ingresar a la plataforma
                </Text>
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="purple" onClick={handleCreate} isLoading={creating}>
              Registrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
